/**
 * Quota-safe sessionStorage adapter for pinia-plugin-persist.
 *
 * The plugin re-serializes the whole persisted slice on every mutation from inside a
 * `$subscribe` callback, which Vue runs during its update flush. Safari's Web Storage
 * quota is small enough that `setItem` can throw `QuotaExceededError` there, and that
 * exception aborts the flush queue: state keeps changing but the DOM never re-renders
 * again (dead nav tabs, stale/hazy dashboard). Nothing here may throw into Vue.
 */

/** Persisted lists that are large and always re-fetched from the API, so they are safe to drop. */
const REFETCHABLE_PATHS = ['userCourses', 'userResources']

/** Keys whose full payload already blew the quota, so later writes go straight to the trimmed one. */
const keysOverQuota = new Set()

let hasWarnedAboutStorage = false

function warnOnce(message, error) {
  if (hasWarnedAboutStorage) return
  hasWarnedAboutStorage = true
  console.warn(`[safePersistStorage] ${message}`, error)
}

function sessionStorageOrNull() {
  try {
    return typeof window !== 'undefined' ? window.sessionStorage ?? null : null
  } catch (error) {
    // Safari with cookies/website-data blocked throws on the property access itself.
    warnOnce('sessionStorage is unavailable; store state will not persist.', error)
    return null
  }
}

function isQuotaExceededError(error) {
  if (!error) return false
  return (
    error.name === 'QuotaExceededError' ||
    error.name === 'NS_ERROR_DOM_QUOTA_REACHED' ||
    error.name === 'QUOTA_EXCEEDED_ERR' ||
    error.code === 22 ||
    error.code === 1014
  )
}

/**
 * Re-serialize `value` without the bulky refetchable slices.
 * @returns {string} the smaller payload, or `value` unchanged when there was nothing to shed.
 */
function withoutRefetchablePaths(value) {
  let parsed
  try {
    parsed = JSON.parse(value)
  } catch (error) {
    return value
  }
  if (!parsed || typeof parsed !== 'object') return value

  const shedPaths = REFETCHABLE_PATHS.filter((path) => parsed[path] != null)
  if (!shedPaths.length) return value
  shedPaths.forEach((path) => delete parsed[path])

  try {
    return JSON.stringify(parsed)
  } catch (error) {
    return value
  }
}

export const safeSessionStorage = {
  getItem(key) {
    const storage = sessionStorageOrNull()
    if (!storage) return null
    try {
      return storage.getItem(key)
    } catch (error) {
      warnOnce(`Could not read "${key}" from sessionStorage.`, error)
      return null
    }
  },

  setItem(key, value) {
    const storage = sessionStorageOrNull()
    if (!storage) return

    if (!keysOverQuota.has(key)) {
      try {
        storage.setItem(key, value)
        return
      } catch (error) {
        if (!isQuotaExceededError(error)) {
          warnOnce(`Could not write "${key}" to sessionStorage.`, error)
          return
        }
        keysOverQuota.add(key)
      }
    }

    const trimmed = withoutRefetchablePaths(value)
    if (trimmed !== value) {
      try {
        storage.setItem(key, trimmed)
        warnOnce(
          `sessionStorage quota reached; persisting "${key}" without ${REFETCHABLE_PATHS.join(', ')}.`,
        )
        return
      } catch (error) {
        if (!isQuotaExceededError(error)) {
          warnOnce(`Could not write trimmed "${key}" to sessionStorage.`, error)
          return
        }
      }
    } else if (keysOverQuota.has(key)) {
      // Nothing bulky left to shed, so the untried full payload is all there is.
      try {
        storage.setItem(key, value)
        return
      } catch (error) {
        if (!isQuotaExceededError(error)) {
          warnOnce(`Could not write "${key}" to sessionStorage.`, error)
          return
        }
      }
    }

    // Even the trimmed payload does not fit. A missing entry just means the store hydrates
    // from its defaults and refetches, which is far better than breaking Vue's render loop.
    safeSessionStorage.removeItem(key)
    warnOnce(`sessionStorage quota reached; dropped persisted "${key}" entirely.`)
  },

  removeItem(key) {
    const storage = sessionStorageOrNull()
    if (!storage) return
    try {
      storage.removeItem(key)
    } catch (error) {
      warnOnce(`Could not remove "${key}" from sessionStorage.`, error)
    }
  },

  clear() {
    const storage = sessionStorageOrNull()
    if (!storage) return
    try {
      storage.clear()
    } catch (error) {
      warnOnce('Could not clear sessionStorage.', error)
    }
  },
}

export default safeSessionStorage
